import { createContext, useContext, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react'
import { itemsForDataset, type Dataset, type DatasetItem, type GoldenExtraction } from './mockData'
import { LOCAL_DATASET_PREFIX, LOCAL_ITEM_PREFIX, loadAllDatasets, saveLocalDatasets } from './localStore'

/** Fields captured when adding a call as a dataset item. */
export type NewItemInput = {
  callId: string
  /** ISO timestamp of the call. */
  callDate: string
  callerName: string
  transcriptSnapshot: string
  tags: string[]
  golden: GoldenExtraction
}

/** Fields captured when creating a dataset. */
export type NewDatasetInput = {
  name: string
  description: string
  tag: string
}

/**
 * Session-wide prototype state for the Ops → Datasets page. Mounted once by
 * `OpsDatasetsLayoutComponent` so the whole page reads and mutates ONE in-memory
 * store. A dataset is a tag query over items (many-to-many), so `itemsFor` resolves
 * membership by tag. Locally created datasets and items are saved in this browser
 * (`localStore.ts`) so Ops → Evals can run them; this provider is the single seam to
 * swap for real GraphQL later.
 * See docs/plans/2026-09-11-prompt-eval-system-plan.md §4 and §8.
 */
export type OpsDatasetsContextValue = {
  datasets: Dataset[]
  items: DatasetItem[]
  /** Items belonging to a dataset, resolved by tag membership. */
  itemsFor: (datasetId: string) => DatasetItem[]
  /** Create a new (empty) dataset and return its id. */
  createDataset: (input: NewDatasetInput) => string
  /** Add a call as a new labeled item, returning its id. */
  addItem: (input: NewItemInput) => string
  /** Replace an item's golden extraction. */
  updateGolden: (itemId: string, golden: GoldenExtraction) => void
}

const OpsDatasetsContext = createContext<OpsDatasetsContextValue | null>(null)

/** Mount keyed by `workspaceId`, so a workspace switch reloads that workspace's datasets. */
export function OpsDatasetsProvider({
  workspaceId,
  children,
}: {
  workspaceId: string | undefined
  children: ReactNode
}): ReactElement {
  const [datasets, setDatasets] = useState<Dataset[]>(() => loadAllDatasets(workspaceId).datasets)
  const [items, setItems] = useState<DatasetItem[]>(() => loadAllDatasets(workspaceId).items)

  useEffect(() => saveLocalDatasets(workspaceId, datasets, items), [workspaceId, datasets, items])

  const value = useMemo<OpsDatasetsContextValue>(() => {
    const itemsFor = (datasetId: string) => {
      const dataset = datasets.find((entry) => entry.id === datasetId)
      return dataset ? itemsForDataset(dataset, items) : []
    }

    const createDataset = (input: NewDatasetInput) => {
      const now = new Date().toISOString()
      const id = `${LOCAL_DATASET_PREFIX}${now}`
      setDatasets((prev) => [...prev, { id, ...input, createdBy: `You`, createdAt: now }])
      return id
    }

    const addItem = (input: NewItemInput) => {
      const now = new Date().toISOString()
      const id = `${LOCAL_ITEM_PREFIX}${now}`
      setItems((prev) => [
        {
          id,
          callId: input.callId,
          callDate: input.callDate,
          callerName: input.callerName,
          transcriptSnapshot: input.transcriptSnapshot,
          golden: input.golden,
          tags: input.tags,
          labeledBy: `You`,
          labeledAt: now,
          provenance: { model: `gpt-5-mini`, temperature: 0, promptVersions: {} },
        },
        ...prev,
      ])
      return id
    }

    const updateGolden = (itemId: string, golden: GoldenExtraction) => {
      const now = new Date().toISOString()
      setItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, golden, labeledBy: `You`, labeledAt: now } : item)),
      )
    }

    return { datasets, items, itemsFor, createDataset, addItem, updateGolden }
  }, [datasets, items])

  return <OpsDatasetsContext.Provider value={value}>{children}</OpsDatasetsContext.Provider>
}

export function useOpsDatasetsData(): OpsDatasetsContextValue {
  const value = useContext(OpsDatasetsContext)
  if (!value) {
    throw new Error(`useOpsDatasetsData must be used within an OpsDatasetsProvider`)
  }
  return value
}
